import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "overview-company-block" }
const _hoisted_2 = { class: "overview-company-block__company" }
const _hoisted_3 = {
  key: 1,
  class: "typographies-paragraph-medium-regular overview-company-block__address"
}
const _hoisted_4 = { key: 0 }

import { computed, inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { type RouteLocationRaw } from 'vue-router';
import CompaniesService from 'api/companies';
import { getIndividualAddresses } from 'api/individuals';
import EmailLinkCompass from 'vcomponents/overview/tabs/data/EmailLinkCompass.vue';
import PhoneLinkCompass from 'vcomponents/overview/tabs/data/PhoneLinkCompass.vue';
import DrawerAccordion from 'vcomponents/common/drawer/accordion/DrawerAccordion.vue';
import type { Translator, PushToast } from 'js/shims-vue';

type LinkedType = 'third' | 'opportunity' | 'people';

interface Address {
    address_line_1?: string;
    address_line_2?: string;
    address_line_3?: string;
    address_line_4?: string;
    postal_code?: string;
    city?: string;
    is_invoicing_address?: boolean;
}

interface Company {
    id?: number;
    name?: string;
    email?: string;
    phone?: string;
    link?: RouteLocationRaw;
    type?: string;
    relationType?: string;
}

interface InfoItem {
    key: string;
    value: string | number | boolean | null;
}

interface Acl {
    update_individuals?: boolean;
    update_suppliers?: boolean;
    update_prospects?: boolean;
    update_clients?: boolean;
}

interface Permissions {
    acl: Acl;
}

interface AddressesResponse {
    data: { data: Address[] };
}

interface ThirdLegacy {
    edit(id?: number): void;
}

interface Props {
    linkedtype: LinkedType;
    linkedid: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OverviewCompanyBlock',
  props: {
    linkedtype: {},
    linkedid: {}
  },
  setup(__props: any) {

const props = __props;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();

const translations = {
    title: $t.__('overview.companyBlock.title'),
    edit: $t.__('common.actions.edit'),
    seeTheFile: $t.__('common.actions.see_the_file'),
    email: $t.__('common.labels.email'),
    phone: $t.__('common.labels.phone'),
    address: $t.__('common.labels.address'),
    fetchError: $t.__('common.labels.toast.error'),
};

const address = ref<Address>({});
const isLoading = ref<boolean>(true);

const permissions = computed<Permissions>(() => store.getters['user/getPermissions']);

const company = computed<Company>(() => {
    if (props.linkedtype === 'third') {
        return cleanCompanyDetails(store.state.third.dataFetch.informations);
    }
    if (props.linkedtype === 'people') {
        return store.state.people.dataFetch.thirds?.[0] ?? {};
    }
    return cleanCompanyDetails(store.state.opportunity.dataFetch.third);
});

const companyLink = computed<RouteLocationRaw | undefined>(() => {
    if (props.linkedtype === 'third') {
        return store.state.third.identity.link;
    }
    return company.value.link;
});

const hasCompany = computed<boolean>(() => Object.keys(company.value).length > 0);
const hasAddress = computed<boolean>(() => Object.keys(address.value).length > 0);

const addressLines = computed<string[]>(() => {
    if (!hasAddress.value) {
        return [];
    }
    const lastLine = [address.value.postal_code, address.value.city].filter(Boolean).join(' ');
    return [
        address.value.address_line_1,
        address.value.address_line_2,
        address.value.address_line_3,
        address.value.address_line_4,
        lastLine,
    ].filter((line): line is string => Boolean(line));
});

const id = computed<number | undefined>(() => {
    if (props.linkedtype === 'third') {
        return props.linkedid;
    }
    return company.value.id;
});

const isIndividual = computed<boolean>(
    () =>
        (props.linkedtype === 'third' && store.state.third.identity.isCorporation === false) ||
        (props.linkedtype === 'opportunity' && company.value.type === 'person') ||
        (props.linkedtype === 'people' && company.value.relationType === 'individuals'),
);

const isSupplier = computed<boolean>(
    () =>
        (props.linkedtype === 'third' && store.state.third.identity.type === 'supplier') ||
        (props.linkedtype === 'people' && company.value.type === 'supplier'),
);

const isProspect = computed<boolean>(
    () =>
        (props.linkedtype === 'third' && store.state.third.identity.type === 'prospect') ||
        (props.linkedtype === 'people' && company.value.type === 'prospect'),
);

const showEditButton = computed<boolean>(() => {
    const acl = permissions.value.acl;
    if (isIndividual.value) {
        return Boolean(acl.update_individuals);
    }
    if (isSupplier.value) {
        return Boolean(acl.update_suppliers);
    }
    if (isProspect.value) {
        return Boolean(acl.update_prospects);
    }
    return Boolean(acl.update_clients);
});

const cleanCompanyDetails = (rawCompany: InfoItem[] | null | undefined): Company => {
    if (!rawCompany) {
        return {};
    }
    return Object.fromEntries(rawCompany.map((item) => [item.key, item.value]));
};

const handleEdit = (): void => {
    (Third as ThirdLegacy).edit(id.value);
};

const loadAddresses = async (): Promise<void> => {
    if (!hasCompany.value) {
        isLoading.value = false;
        return;
    }
    const fetchAddresses = isIndividual.value
        ? getIndividualAddresses(id.value)
        : CompaniesService.getCompanyAddresses(id.value);
    try {
        const response = (await fetchAddresses) as AddressesResponse;
        if (response.data.data.length) {
            address.value = response.data.data.find((item) => item.is_invoicing_address) ?? {};
        }
    } catch {
        $pushToast({ type: 'error', icon: true, text: translations.fetchError });
    } finally {
        isLoading.value = false;
    }
};

onMounted(loadAddresses);

return (_ctx: any,_cache: any) => {
  const _component_CompassPlainButton = _resolveComponent("CompassPlainButton")!
  const _component_CompassObject = _resolveComponent("CompassObject")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_CompassTooltip = _resolveComponent("CompassTooltip")!
  const _component_CompassDescriptionListItem = _resolveComponent("CompassDescriptionListItem")!
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!
  const _component_CompassDescriptionList = _resolveComponent("CompassDescriptionList")!

  return (hasCompany.value)
    ? (_openBlock(), _createBlock(DrawerAccordion, {
        key: 0,
        id: "corp",
        title: translations.title,
        coloredIcon: "company",
        openByDefault: ""
      }, {
        action: _withCtx(() => [
          (showEditButton.value)
            ? (_openBlock(), _createBlock(_component_CompassPlainButton, {
                key: 0,
                label: translations.edit,
                size: _ctx.CompassEnums.PlainButtonSize.SMALL,
                variant: _ctx.CompassEnums.PlainButtonVariantType.NEUTRAL,
                onClick: _withModifiers(handleEdit, ["stop"])
              }, null, 8 /* PROPS */, ["label", "size", "variant"]))
            : _createCommentVNode("v-if", true)
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_CompassTooltip, {
                content: translations.seeTheFile,
                arrow: false,
                placement: "right-start"
              }, {
                default: _withCtx(() => [
                  (companyLink.value)
                    ? (_openBlock(), _createBlock(_component_RouterLink, {
                        key: 0,
                        to: companyLink.value
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_CompassObject, {
                            size: _ctx.CompassEnums.AvatarSize.EXTRA_SMALL,
                            label: company.value.name,
                            isLink: ""
                          }, null, 8 /* PROPS */, ["size", "label"])
                        ]),
                        _: 1 /* STABLE */
                      }, 8 /* PROPS */, ["to"]))
                    : _createCommentVNode("v-if", true)
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["content"])
            ]),
            _createVNode(_component_CompassDescriptionList, {
              variant: _ctx.CompassEnums.DescriptionListVariantType.FROZEN_FIELD
            }, {
              default: _withCtx(() => [
                (company.value.email)
                  ? (_openBlock(), _createBlock(_component_CompassDescriptionListItem, {
                      key: 0,
                      label: translations.email
                    }, {
                      default: _withCtx(() => [
                        _createVNode(EmailLinkCompass, {
                          email: company.value.email,
                          linkedid: __props.linkedid,
                          linkedtype: __props.linkedtype
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(company.value.email), 1 /* TEXT */)
                          ]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["email", "linkedid", "linkedtype"])
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["label"]))
                  : _createCommentVNode("v-if", true),
                (company.value.phone)
                  ? (_openBlock(), _createBlock(_component_CompassDescriptionListItem, {
                      key: 1,
                      label: translations.phone
                    }, {
                      default: _withCtx(() => [
                        _createVNode(PhoneLinkCompass, {
                          phoneNumber: company.value.phone
                        }, null, 8 /* PROPS */, ["phoneNumber"])
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["label"]))
                  : _createCommentVNode("v-if", true),
                (hasAddress.value)
                  ? (_openBlock(), _createBlock(_component_CompassDescriptionListItem, {
                      key: 2,
                      label: translations.address
                    }, {
                      default: _withCtx(() => [
                        (isLoading.value)
                          ? (_openBlock(), _createBlock(_component_CompassSkeleton, {
                              key: 0,
                              variant: _ctx.CompassEnums.SkeletonVariantType.LINE,
                              lines: 2
                            }, null, 8 /* PROPS */, ["variant"]))
                          : _createCommentVNode("v-if", true),
                        (!isLoading.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(addressLines.value, (line, index) => {
                                return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                                  _createTextVNode(_toDisplayString(line), 1 /* TEXT */),
                                  (index < addressLines.value.length - 1)
                                    ? (_openBlock(), _createElementBlock("br", _hoisted_4))
                                    : _createCommentVNode("v-if", true)
                                ], 64 /* STABLE_FRAGMENT */))
                              }), 128 /* KEYED_FRAGMENT */))
                            ]))
                          : _createCommentVNode("v-if", true)
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["label"]))
                  : _createCommentVNode("v-if", true)
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["variant"])
          ])
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["title"]))
    : _createCommentVNode("v-if", true)
}
}

})